<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useSearchableDropdown } from 'dashboard/composables/useSearchableDropdown';
import { useMultiSelectLabel } from 'dashboard/composables/useMultiSelectLabel';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';

const props = defineProps({
  initialFilters: { type: Object, default: () => ({}) },
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['filterChange', 'open']);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const { avatarColor, initials } = useAvatarColor();

function initAssigneeIds(val) {
  if (Array.isArray(val)) return val;
  if (val) return [val];
  return [];
}

const filters = ref({
  assignee_id: initAssigneeIds(props.initialFilters.assignee_id),
  status: props.initialFilters.status ?? '',
  priority: props.initialFilters.priority ?? [],
  task_type: props.initialFilters.task_type ?? [],
});

const openDropdown = ref(null);
const dropdownAlign = ref('left'); // 'left' | 'right' — recalculado a cada abertura
const dropdownPos = ref({ top: 0, left: 0 });

function calcFlip(triggerEl, dropdownWidthPx) {
  if (!triggerEl) return 'left';
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  return rect.left + dropdownWidthPx > window.innerWidth - margin
    ? 'right'
    : 'left';
}

function calcPos(triggerEl, dropdownWidthPx, align) {
  if (!triggerEl) return { top: 0, left: 0 };
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  const top = rect.bottom + 4;
  const left =
    align === 'right'
      ? Math.max(margin, rect.right - dropdownWidthPx)
      : rect.left;
  return { top, left };
}

// ── searches ──────────────────────────────────────────────────────
const statusSearch = ref('');
const prioritySearch = ref('');
const taskTypeSearch = ref('');

// ── highlighted indexes ───────────────────────────────────────────
const statusHighlight = ref(0);
const priorityHighlight = ref(0);
const taskTypeHighlight = ref(0);

// ── static options ────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { key: 'pending', label: t('TASKS.STATUS.PENDING') },
  { key: 'completed', label: t('TASKS.STATUS.COMPLETED') },
];

const PRIORITY_OPTIONS = [
  { key: 'urgent', label: t('TASKS.PRIORITY.URGENT'), dot: 'text-n-ruby-9' },
  { key: 'high', label: t('TASKS.PRIORITY.HIGH'), dot: 'text-n-amber-9' },
  { key: 'normal', label: t('TASKS.PRIORITY.NORMAL'), dot: 'text-n-blue-9' },
  {
    key: 'no_priority',
    label: t('TASKS.PRIORITY.NONE'),
    dot: 'text-n-slate-8',
  },
];

const TASK_TYPE_OPTIONS = [
  {
    key: 'general',
    label: t('TASKS.TYPES.GENERAL'),
    icon: 'i-lucide-circle-dot',
  },
  { key: 'call', label: t('TASKS.TYPES.CALL'), icon: 'i-lucide-phone' },
  { key: 'email', label: t('TASKS.TYPES.EMAIL'), icon: 'i-lucide-mail' },
  {
    key: 'meeting',
    label: t('TASKS.TYPES.MEETING'),
    icon: 'i-lucide-calendar',
  },
  {
    key: 'follow_up',
    label: t('TASKS.TYPES.FOLLOW_UP'),
    icon: 'i-lucide-refresh-cw',
  },
  {
    key: 'whatsapp',
    label: t('TASKS.TYPES.WHATSAPP'),
    icon: 'i-lucide-message-circle',
  },
  {
    key: 'contract',
    label: t('TASKS.TYPES.CONTRACT'),
    icon: 'i-lucide-file-signature',
  },
  {
    key: 'payment',
    label: t('TASKS.TYPES.PAYMENT'),
    icon: 'i-lucide-banknote',
  },
];

// ── labels ────────────────────────────────────────────────────────
const assigneeTriggerEl = ref(null);
const priorityTriggerEl = ref(null);
const taskTypeTriggerEl = ref(null);
const statusTriggerEl = ref(null);

const assigneeSelectedNames = computed(() => {
  const sel = filters.value.assignee_id;
  return sel.map(id =>
    id === 'unassigned'
      ? t('TASKS.FILTERS.UNASSIGNED')
      : (agents.value?.find(a => a.id === id)?.name ?? '…')
  );
});

const prioritySelectedNames = computed(() =>
  filters.value.priority.map(
    key => PRIORITY_OPTIONS.find(o => o.key === key)?.label ?? '…'
  )
);

const taskTypeSelectedNames = computed(() =>
  filters.value.task_type.map(
    key => TASK_TYPE_OPTIONS.find(o => o.key === key)?.label ?? '…'
  )
);

const assigneeFitLabel = useMultiSelectLabel(
  assigneeTriggerEl,
  assigneeSelectedNames
);
const priorityFitLabel = useMultiSelectLabel(
  priorityTriggerEl,
  prioritySelectedNames
);
const taskTypeFitLabel = useMultiSelectLabel(
  taskTypeTriggerEl,
  taskTypeSelectedNames
);

const assigneeLabel = computed(
  () => assigneeFitLabel.value || t('TASKS.FILTERS.ASSIGNEE')
);

const statusLabel = computed(() => {
  if (!filters.value.status) return t('TASKS.FILTERS.STATUS');
  return (
    STATUS_OPTIONS.find(o => o.key === filters.value.status)?.label ??
    t('TASKS.FILTERS.STATUS')
  );
});

const priorityLabel = computed(
  () => priorityFitLabel.value || t('TASKS.FILTERS.PRIORITY')
);

const taskTypeLabel = computed(
  () => taskTypeFitLabel.value || t('TASKS.FILTERS.TASK_TYPE')
);

const currentTaskTypeIcon = computed(() =>
  filters.value.task_type.length === 1
    ? (TASK_TYPE_OPTIONS.find(o => o.key === filters.value.task_type[0])
        ?.icon ?? null)
    : null
);

const hasActiveFilters = computed(() =>
  Object.entries(filters.value).some(([, v]) =>
    Array.isArray(v) ? v.length > 0 : v !== ''
  )
);

function isAssigneeSelected(id) {
  return filters.value.assignee_id.includes(id);
}

// ── actions ───────────────────────────────────────────────────────
function closeDropdowns() {
  openDropdown.value = null;
}

function applyFilters() {
  const active = Object.fromEntries(
    Object.entries(filters.value).filter(([, v]) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== '' && v !== null;
    })
  );
  emit('filterChange', active);
}

function setAssignee(id) {
  if (id === '') {
    filters.value.assignee_id = [];
    closeDropdowns();
  } else {
    const idx = filters.value.assignee_id.indexOf(id);
    if (idx === -1) filters.value.assignee_id.push(id);
    else filters.value.assignee_id.splice(idx, 1);
  }
  applyFilters();
}

function setStatus(key) {
  filters.value.status = key;
  closeDropdowns();
  applyFilters();
}

function setPriority(key) {
  if (key === '') {
    filters.value.priority = [];
    closeDropdowns();
  } else {
    const idx = filters.value.priority.indexOf(key);
    if (idx === -1) filters.value.priority.push(key);
    else filters.value.priority.splice(idx, 1);
  }
  applyFilters();
}

function setTaskType(key) {
  if (key === '') {
    filters.value.task_type = [];
    closeDropdowns();
  } else {
    const idx = filters.value.task_type.indexOf(key);
    if (idx === -1) filters.value.task_type.push(key);
    else filters.value.task_type.splice(idx, 1);
  }
  applyFilters();
}

// ── filtered lists ────────────────────────────────────────────────
function filterByLabel(list, q, key = 'label') {
  if (!q) return list;
  return list
    .filter(o => o[key].toLowerCase().includes(q.toLowerCase()))
    .sort(
      (a, b) =>
        a[key].toLowerCase().indexOf(q.toLowerCase()) -
        b[key].toLowerCase().indexOf(q.toLowerCase())
    );
}

const ALL_LABEL = 'todos';

function filterAgentsBySearch(q) {
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
}

function buildAssigneeNavigable(search) {
  if (!search)
    return [
      { id: '' },
      ...(agents.value ?? []).map(a => ({ id: a.id })),
      { id: 'unassigned' },
    ];
  const items = filterAgentsBySearch(search.toLowerCase()).map(a => ({
    id: a.id,
  }));
  const showAll = ALL_LABEL.includes(search.toLowerCase());
  const showUnassigned = t('TASKS.FILTERS.UNASSIGNED')
    .toLowerCase()
    .includes(search.toLowerCase());
  return [
    ...(showAll ? [{ id: '' }] : []),
    ...items,
    ...(showUnassigned ? [{ id: 'unassigned' }] : []),
  ];
}

const {
  search: assigneeSearch,
  highlightedIndex: assigneeHighlight,
  handleKeydown: onAssigneeKeydown,
} = useSearchableDropdown({
  items: computed(() => buildAssigneeNavigable(assigneeSearch.value)),
  onSelect: item => setAssignee(item.id),
  onClose: () => {
    openDropdown.value = null;
  },
});

const filteredAgents = computed(() =>
  filterAgentsBySearch(assigneeSearch.value.toLowerCase())
);

const filteredStatus = computed(() =>
  filterByLabel(STATUS_OPTIONS, statusSearch.value)
);
const filteredPriority = computed(() =>
  filterByLabel(PRIORITY_OPTIONS, prioritySearch.value)
);
const filteredTaskType = computed(() =>
  filterByLabel(TASK_TYPE_OPTIONS, taskTypeSearch.value)
);

// ── navigable items ───────────────────────────────────────────────
const assigneeNavigable = computed(() =>
  buildAssigneeNavigable(assigneeSearch.value)
);

const statusNavigable = computed(() => {
  if (!statusSearch.value)
    return [{ id: '' }, ...STATUS_OPTIONS.map(o => ({ id: o.key }))];
  const items = filteredStatus.value.map(o => ({ id: o.key }));
  return ALL_LABEL.includes(statusSearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

const priorityNavigable = computed(() => {
  if (!prioritySearch.value)
    return [{ id: '' }, ...PRIORITY_OPTIONS.map(o => ({ id: o.key }))];
  const items = filteredPriority.value.map(o => ({ id: o.key }));
  return ALL_LABEL.includes(prioritySearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

const taskTypeNavigable = computed(() => {
  if (!taskTypeSearch.value)
    return [{ id: '' }, ...TASK_TYPE_OPTIONS.map(o => ({ id: o.key }))];
  const items = filteredTaskType.value.map(o => ({ id: o.key }));
  return ALL_LABEL.includes(taskTypeSearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

// reset highlight quando lista muda
watch(statusNavigable, () => {
  statusHighlight.value = 0;
});
watch(statusSearch, () => {
  statusHighlight.value = 0;
});
watch(priorityNavigable, () => {
  priorityHighlight.value = 0;
});
watch(prioritySearch, () => {
  priorityHighlight.value = 0;
});
watch(taskTypeNavigable, () => {
  taskTypeHighlight.value = 0;
});
watch(taskTypeSearch, () => {
  taskTypeHighlight.value = 0;
});

// ── keyboard handlers ─────────────────────────────────────────────
function makeKeydownHandler(
  getDropdownName,
  getSearch,
  setSearch,
  getNavigable,
  getHighlight,
  setHighlight,
  selectFn
) {
  return function (e) {
    if (openDropdown.value !== getDropdownName()) return;
    if (e.key === 'Escape') {
      openDropdown.value = null;
      setSearch('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(Math.min(getHighlight() + 1, getNavigable().length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(Math.max(getHighlight() - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = getNavigable()[getHighlight()];
      if (item) selectFn(item.id);
    } else if (e.key === 'Backspace') {
      setSearch(getSearch().slice(0, -1));
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      setSearch(getSearch() + e.key);
    }
  };
}

const onStatusKeydown = makeKeydownHandler(
  () => 'status',
  () => statusSearch.value,
  v => {
    statusSearch.value = v;
  },
  () => statusNavigable.value,
  () => statusHighlight.value,
  v => {
    statusHighlight.value = v;
  },
  setStatus
);

const onPriorityKeydown = makeKeydownHandler(
  () => 'priority',
  () => prioritySearch.value,
  v => {
    prioritySearch.value = v;
  },
  () => priorityNavigable.value,
  () => priorityHighlight.value,
  v => {
    priorityHighlight.value = v;
  },
  setPriority
);

const onTaskTypeKeydown = makeKeydownHandler(
  () => 'task_type',
  () => taskTypeSearch.value,
  v => {
    taskTypeSearch.value = v;
  },
  () => taskTypeNavigable.value,
  () => taskTypeHighlight.value,
  v => {
    taskTypeHighlight.value = v;
  },
  setTaskType
);

const HANDLERS = {
  assignee: onAssigneeKeydown,
  status: onStatusKeydown,
  priority: onPriorityKeydown,
  task_type: onTaskTypeKeydown,
};

watch(openDropdown, (val, old) => {
  if (old && HANDLERS[old])
    window.removeEventListener('keydown', HANDLERS[old]);
  if (val && HANDLERS[val]) window.addEventListener('keydown', HANDLERS[val]);
  // limpa busca do dropdown que fechou
  if (old === 'assignee') assigneeSearch.value = '';
  if (old === 'status') statusSearch.value = '';
  if (old === 'priority') prioritySearch.value = '';
  if (old === 'task_type') taskTypeSearch.value = '';
});

function onClickOutside(e) {
  if (
    !e.target.closest('[data-task-filters]') &&
    !e.target.closest('[data-task-filters-dropdown]')
  )
    closeDropdowns();
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => {
  Object.values(HANDLERS).forEach(h =>
    window.removeEventListener('keydown', h)
  );
  document.removeEventListener('mousedown', onClickOutside);
});

// ── helpers de highlight no template ─────────────────────────────
function isHighlighted(navigable, id, highlightIdx) {
  return navigable.findIndex(i => i.id === id) === highlightIdx;
}

// ── actions ───────────────────────────────────────────────────────
const DROPDOWN_WIDTHS = {
  assignee: 176, // w-44
  status: 160, // w-40
  priority: 160, // w-40
  task_type: 144, // w-36
};

function toggleDropdown(name, triggerEl) {
  const isOpening = openDropdown.value !== name;
  openDropdown.value = isOpening ? name : null;
  if (isOpening) {
    const width = DROPDOWN_WIDTHS[name];
    dropdownAlign.value = calcFlip(triggerEl, width);
    dropdownPos.value = calcPos(triggerEl, width, dropdownAlign.value);
    emit('open');
  }
}

defineExpose({ closeAll: closeDropdowns });

function resetFilters() {
  filters.value = { assignee_id: [], status: '', priority: [], task_type: [] };
  closeDropdowns();
  emit('filterChange', {});
}

const sz = computed(() =>
  props.compact
    ? {
        btn: 'text-xs px-2 py-1',
        item: 'text-xs px-2 py-1.5',
        chevron: 'size-3',
      }
    : {
        btn: 'text-sm px-2.5 py-1.5',
        item: 'text-sm px-3 py-2',
        chevron: 'size-3.5',
      }
);
</script>

<template>
  <div data-task-filters class="flex items-center gap-2">
    <!-- ── Responsável ── -->
    <div class="relative flex-shrink-0" @click.stop>
      <button
        ref="assigneeTriggerEl"
        class="flex items-center gap-1 border rounded-lg transition-colors focus:outline-none"
        :class="[
          sz.btn,
          filters.assignee_id.length
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container',
        ]"
        @click="toggleDropdown('assignee', $event.currentTarget)"
      >
        <span
          v-if="openDropdown === 'assignee' && assigneeSearch"
          class="text-n-slate-10"
          >{{ assigneeSearch }}</span
        >
        <span v-else>{{ assigneeLabel }}</span>
        <span
          class="ml-0.5 text-n-slate-10 i-lucide-chevron-down"
          :class="[sz.chevron]"
        />
      </button>
    </div>

    <!-- ── Status ── -->
    <div class="relative flex-shrink-0" @click.stop>
      <button
        ref="statusTriggerEl"
        class="flex items-center gap-1 border rounded-lg transition-colors focus:outline-none"
        :class="[
          sz.btn,
          filters.status
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container',
        ]"
        @click="toggleDropdown('status', $event.currentTarget)"
      >
        <span
          v-if="openDropdown === 'status' && statusSearch"
          class="text-n-slate-10"
          >{{ statusSearch }}</span
        >
        <span v-else>{{ statusLabel }}</span>
        <span
          class="ml-0.5 text-n-slate-10 i-lucide-chevron-down"
          :class="[sz.chevron]"
        />
      </button>
    </div>

    <!-- ── Prioridade ── -->
    <div class="relative flex-shrink-0" @click.stop>
      <button
        ref="priorityTriggerEl"
        class="flex items-center gap-1 border rounded-lg transition-colors focus:outline-none"
        :class="[
          sz.btn,
          filters.priority.length
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container',
        ]"
        @click="toggleDropdown('priority', $event.currentTarget)"
      >
        <span
          v-if="openDropdown === 'priority' && prioritySearch"
          class="text-n-slate-10"
          >{{ prioritySearch }}</span
        >
        <span v-else>{{ priorityLabel }}</span>
        <span
          class="ml-0.5 text-n-slate-10 i-lucide-chevron-down"
          :class="[sz.chevron]"
        />
      </button>
    </div>

    <!-- ── Tipo ── -->
    <div class="relative flex-shrink-0" @click.stop>
      <button
        ref="taskTypeTriggerEl"
        class="flex items-center gap-1 border rounded-lg transition-colors focus:outline-none"
        :class="[
          sz.btn,
          filters.task_type.length
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container',
        ]"
        @click="toggleDropdown('task_type', $event.currentTarget)"
      >
        <span
          v-if="openDropdown === 'task_type' && taskTypeSearch"
          class="text-n-slate-10"
          >{{ taskTypeSearch }}</span
        >
        <template v-else>
          <span
            v-if="currentTaskTypeIcon"
            class="size-3.5"
            :class="currentTaskTypeIcon"
          />
          {{ taskTypeLabel }}
        </template>
        <span
          class="ml-0.5 text-n-slate-10 i-lucide-chevron-down"
          :class="[sz.chevron]"
        />
      </button>
    </div>

    <!-- ── Reset ── -->
    <button
      v-if="hasActiveFilters"
      class="text-n-slate-10 hover:text-n-slate-12 px-1.5 py-1.5 flex-shrink-0"
      :class="compact ? 'text-xs' : 'text-sm'"
      :title="t('TASKS.FILTERS.RESET')"
      @click="resetFilters"
    >
      <span class="i-lucide-x size-3.5" />
    </button>
  </div>

  <Teleport to="body">
    <!-- ── Dropdown: Responsável ── -->
    <div
      v-if="openDropdown === 'assignee'"
      data-task-filters-dropdown
      class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="assigneeNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="[
            sz.item,
            isHighlighted(assigneeNavigable, '', assigneeHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !filters.assignee_id.length
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12',
          ]"
          @click="setAssignee('')"
        >
          {{ t('TASKS.ALL') }}
        </button>
      </template>
      <button
        v-for="agent in filteredAgents"
        :key="agent.id"
        class="w-full flex items-center gap-2 hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="[
          sz.item,
          isHighlighted(assigneeNavigable, agent.id, assigneeHighlight)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : isAssigneeSelected(agent.id)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12',
        ]"
        @click="setAssignee(agent.id)"
      >
        <div
          class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
          :style="
            agent.thumbnail
              ? {
                  backgroundImage: `url(${agent.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { backgroundColor: avatarColor(agent.name) }
          "
        >
          <span v-if="!agent.thumbnail">{{ initials(agent.name) }}</span>
        </div>
        <span class="truncate">{{ agent.name }}</span>
        <span
          v-if="isAssigneeSelected(agent.id)"
          class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
        />
      </button>
      <p
        v-if="assigneeSearch && !assigneeNavigable.length"
        class="text-n-slate-10 italic"
        :class="sz.item"
      >
        {{ t('TASKS.FILTERS.NO_RESULTS') }}
      </p>
      <template v-if="assigneeNavigable.some(i => i.id === 'unassigned')">
        <div class="border-t border-n-weak my-1" />
        <button
          class="w-full flex items-center gap-2 hover:bg-n-alpha-1 transition-colors focus:outline-none whitespace-nowrap"
          :class="[
            sz.item,
            isHighlighted(assigneeNavigable, 'unassigned', assigneeHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : isAssigneeSelected('unassigned')
                ? 'text-n-brand font-medium'
                : 'text-n-slate-11',
          ]"
          @click="setAssignee('unassigned')"
        >
          <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
          {{ t('TASKS.FILTERS.UNASSIGNED') }}
          <span
            v-if="isAssigneeSelected('unassigned')"
            class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
          />
        </button>
      </template>
    </div>

    <!-- ── Dropdown: Status ── -->
    <div
      v-if="openDropdown === 'status'"
      data-task-filters-dropdown
      class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-40 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="statusNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="[
            sz.item,
            isHighlighted(statusNavigable, '', statusHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !filters.status
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12',
          ]"
          @click="setStatus('')"
        >
          {{ t('TASKS.ALL') }}
        </button>
      </template>
      <button
        v-for="opt in filteredStatus"
        :key="opt.key"
        class="w-full flex items-center hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="[
          sz.item,
          isHighlighted(statusNavigable, opt.key, statusHighlight)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : filters.status === opt.key
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12',
        ]"
        @click="setStatus(opt.key)"
      >
        {{ opt.label }}
      </button>
      <p
        v-if="statusSearch && !statusNavigable.length"
        class="text-n-slate-10 italic"
        :class="sz.item"
      >
        {{ t('TASKS.FILTERS.NO_RESULTS') }}
      </p>
    </div>

    <!-- ── Dropdown: Prioridade ── -->
    <div
      v-if="openDropdown === 'priority'"
      data-task-filters-dropdown
      class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-40 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="priorityNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="[
            sz.item,
            isHighlighted(priorityNavigable, '', priorityHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !filters.priority.length
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12',
          ]"
          @click="setPriority('')"
        >
          {{ t('TASKS.ALL_F') }}
        </button>
      </template>
      <button
        v-for="opt in filteredPriority"
        :key="opt.key"
        class="w-full flex items-center gap-2 hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="[
          sz.item,
          isHighlighted(priorityNavigable, opt.key, priorityHighlight)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : filters.priority.includes(opt.key)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12',
        ]"
        @click="setPriority(opt.key)"
      >
        <span class="i-lucide-flag size-3 flex-shrink-0" :class="opt.dot" />
        <span class="flex-1 text-left">{{ opt.label }}</span>
        <span
          v-if="filters.priority.includes(opt.key)"
          class="i-lucide-check size-3 text-n-brand flex-shrink-0"
        />
      </button>
      <p
        v-if="prioritySearch && !priorityNavigable.length"
        class="text-n-slate-10 italic"
        :class="sz.item"
      >
        {{ t('TASKS.FILTERS.NO_RESULTS') }}
      </p>
    </div>

    <!-- ── Dropdown: Tipo ── -->
    <div
      v-if="openDropdown === 'task_type'"
      data-task-filters-dropdown
      class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-36 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="taskTypeNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="[
            sz.item,
            isHighlighted(taskTypeNavigable, '', taskTypeHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !filters.task_type.length
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12',
          ]"
          @click="setTaskType('')"
        >
          {{ t('TASKS.ALL') }}
        </button>
      </template>
      <button
        v-for="opt in filteredTaskType"
        :key="opt.key"
        class="w-full flex items-center gap-2 hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="[
          sz.item,
          isHighlighted(taskTypeNavigable, opt.key, taskTypeHighlight)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : filters.task_type.includes(opt.key)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12',
        ]"
        @click="setTaskType(opt.key)"
      >
        <span class="size-3.5 flex-shrink-0" :class="opt.icon" />
        {{ opt.label }}
        <span
          v-if="filters.task_type.includes(opt.key)"
          class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
        />
      </button>
      <p
        v-if="taskTypeSearch && !taskTypeNavigable.length"
        class="text-n-slate-10 italic"
        :class="sz.item"
      >
        {{ t('TASKS.FILTERS.NO_RESULTS') }}
      </p>
    </div>
  </Teleport>
</template>
